/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { NativeModules, Platform } from 'react-native';
import { createRemoteConfigClient } from './remoteConfigClient';
import { getDisplayCurrencyForUI } from '../util/currencyConversion';

export type AuthCredentials = {
  username: string;
  password: string;
};

export type AnonymousSsoCredentials = {
  accessToken: string;
  refreshToken: string;
  userId: string;
};

/**
 * Firebase web SDK configuration, resolved by each host app (web only).
 * The native Remote Config implementation reads from the host super-app's
 * `NativeRemoteConfig` bridge and ignores this.
 */
export type FirebaseWebConfig = {
  apiKey?: string;
  authDomain?: string;
  projectId?: string;
  storageBucket?: string;
  messagingSenderId?: string;
  appId?: string;
  measurementId?: string;
};

/**
 * Async accessors for Remote Config values, exposed through the provider.
 * Each resolves to the requested type (defaulting to `''` / `false` / `0`
 * when the source is unavailable).
 */
export type RemoteConfigGetters = {
  getRemoteConfigString: (key: string) => Promise<string>;
  getRemoteConfigBoolean: (key: string) => Promise<boolean>;
  getRemoteConfigNumber: (key: string) => Promise<number>;
};

export type FlightConfig = {
  channelHash?: string;
  channelHashLabel?: string;
  mmbChannelHash?: string;
  gaId?: string;
  auth?: AuthCredentials;
  anonymousSso?: AnonymousSsoCredentials;
  consumerId?: string;
  geoId?: string;
  env?: string;
  hostname?: string;
  // Web only; the native impl ignores it and uses the NativeRemoteConfig bridge.
  firebaseConfig?: FirebaseWebConfig;
  onChannelHashChange?: (hash?: string) => void;
  currency?: string;
  isDistressflights?: boolean;
  displayCurrency?: string;
  productCode?: string;
};

type FlightConfigContextType = {
  config: FlightConfig;
  updateConfig: (updates: Partial<FlightConfig>) => void;
} & RemoteConfigGetters;

export const FlightConfigContext = createContext<
  FlightConfigContextType | undefined
>(undefined);

type FlightConfigProviderProps = FlightConfig & {
  children: ReactNode;
};

export const FlightConfigProvider: React.FC<FlightConfigProviderProps> = ({
  children,
  ...initialConfig
}) => {
  const [config, setConfig] = useState<FlightConfig>(initialConfig);

  const {
    channelHash,
    mmbChannelHash,
    gaId,
    consumerId,
    geoId,
    env,
    auth,
    anonymousSso,
    hostname,
    currency,
    isDistressflights,
  } = initialConfig;
  const authStr = JSON.stringify(auth);
  const ssoStr = JSON.stringify(anonymousSso);

  useEffect(() => {
    setConfig((prev) => {
      let changed = false;
      const next = { ...prev };

      if (gaId !== prev.gaId) {
        next.gaId = gaId;
        changed = true;
      }
      if (channelHash !== prev.channelHash) {
        next.channelHash = channelHash;
        changed = true;
      }
      if (mmbChannelHash !== prev.mmbChannelHash) {
        next.mmbChannelHash = mmbChannelHash;
        changed = true;
      }
      if (isDistressflights !== prev.isDistressflights) {
        next.isDistressflights = isDistressflights;
        changed = true;
      }
      if (consumerId !== prev.consumerId) {
        next.consumerId = consumerId;
        changed = true;
      }
      if (geoId !== prev.geoId) {
        next.geoId = geoId;
        changed = true;
      }
      if (currency !== prev.currency) {
        next.currency = currency;
        changed = true;
      }
      if (env !== prev.env) {
        next.env = env;
        changed = true;
      }
      // Only sync a prop-provided hostname; undefined means "let native resolve it".
      if (hostname !== undefined && hostname !== prev.hostname) {
        next.hostname = hostname;
        changed = true;
      }
      if (JSON.stringify(prev.auth) !== authStr) {
        next.auth = auth;
        changed = true;
      }
      if (JSON.stringify(prev.anonymousSso) !== ssoStr) {
        next.anonymousSso = anonymousSso;
        changed = true;
      }

      return changed ? next : prev;
    });
  }, [
    gaId,
    channelHash,
    mmbChannelHash,
    consumerId,
    geoId,
    env,
    hostname,
    authStr,
    ssoStr,
    auth,
    anonymousSso,
    currency,
    isDistressflights,
  ]);

  // On native, resolve the SSO origin once at mount when the host app hasn't
  // provided a hostname. NativeSSO.authUser is the only RN-accessible bridge
  // that exposes Secrets.SSOOrigin from the host app's native layer.
  useEffect(() => {
    if (Platform.OS === 'web') return;
    if (initialConfig.hostname) return;
    let isMounted = true;
    try {
      NativeModules.NativeSSO?.authUser(
        (_: boolean, details?: Record<string, unknown>) => {
          if (!isMounted) return;
          const origin = details?.origin;
          if (typeof origin !== 'string') return;
          setConfig((prev) =>
            prev.hostname ? prev : { ...prev, hostname: origin },
          );
        },
      );
    } catch {
      // NativeSSO unavailable — hostname stays undefined, checkout will fail
      // validation rather than silently send a wrong origin.
    }
    return () => {
      isMounted = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateConfig = useCallback((updates: Partial<FlightConfig>) => {
    setConfig((prev) => {
      if (
        updates.channelHash !== undefined &&
        updates.channelHash !== prev.channelHash
      ) {
        prev.onChannelHashChange?.(updates.channelHash);
      }

      return { ...prev, ...updates };
    });
  }, []);

  const remoteConfig = useMemo(
    () => createRemoteConfigClient(config.firebaseConfig, config.env),
    [config.firebaseConfig, config.env],
  );

  const value = useMemo(
    () => ({
      config: {
        ...config,
        displayCurrency: getDisplayCurrencyForUI(config.currency),
      },
      updateConfig,
      ...remoteConfig,
    }),
    [config, updateConfig, remoteConfig],
  );

  return (
    <FlightConfigContext.Provider value={value}>
      {children}
    </FlightConfigContext.Provider>
  );
};

export const useFlightConfig = (): FlightConfigContextType => {
  const ctx = useContext(FlightConfigContext);
  if (!ctx)
    throw new Error('useFlightConfig must be used inside FlightConfigProvider');

  return ctx;
};
